import AddIcon from '@mui/icons-material/Add';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { Button, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import OneSchemaImporter from '@oneschema/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useMemo, useRef, useState } from 'react';
import { CompanySearch } from '../../../../components/CompanySearch/CompanySearch';
import { useModalState } from '../../../../components/Modal/ModalContext';
import { useToastMessageState } from '../../../../components/ToastMessage/ToastMessageProvider';
import { ZINDEX } from '../../../../constants/styles';
import { ListType } from '../../../../data-models/list.data-model';
import { useOutsideClick } from '../../../../hooks/useOutsideClick';
import { CompanySearchResponseWithRealId } from '../../../../schemas/CompanySearchResponse.schema';
import { PermissionKey } from '../../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../../services/PermissionService';
import { useListUploadActions } from '../../../../services/hooks/useListActions';
import { getEnvVariable, isDevelopment, isLocal } from '../../../../util/environment';
import { MaggieFeatureFlags } from '../../../../util/feature-flags';
import { getListUploadWebhookKey } from '../../../../util/oneschema';
import {
  newListCompanyFormDataAtom,
  showCreateListCompanyDialogAtom,
} from '../../Companies3/ListForms2/NewListCompanyForm';
import {
  activeCompanyListIdStateJ,
  activeListStateJ,
  companyListStateJ,
} from '../../../../services/state/ListsStateJ';
import { CreateListItemForm } from '../CreateListItemForm/CreateListItemForm';
import { createCompanyDataModel } from '../../../../data-models/company.data-model';
import { templatesTypeListStateLoadableJ } from '../../../../services/state/AdminPanel/CustomFieldsStateJ';

const ButtonAndSearchContainer = styled('div')<{ outlined?: boolean }>`
  display: flex;
  justify-content: end;
  padding: 1rem 0;
  border-radius: 4px;
  min-width: max(23rem, 100%);
  ${({ outlined }) =>
    outlined &&
    'background: white; box-shadow: 0px -4px 40px rgba(255, 255, 255, 0.1), 0px 4px 24px rgba(16, 37, 62, 0.06), 0px 24px 48px rgba(75, 82, 93, 0.04); padding: 1rem;'}
`;

export const ListUploadButtons = () => {
  const templatesTypeListLoadable = useAtomValue(templatesTypeListStateLoadableJ);
  const templatesTypeList =
    templatesTypeListLoadable.state === 'hasData' ? templatesTypeListLoadable.data : null;
  const oneSchemaClientId = getEnvVariable('VITE_ONESCHEMA_CLIENT_ID');
  const webhookKey = getListUploadWebhookKey();

  const activeListId = useAtomValue(activeCompanyListIdStateJ);
  const activeList = useAtomValue(activeListStateJ);

  const list = useAtomValue(companyListStateJ(activeListId));
  const [showImportModal, setShowImportModal] = useState(false);
  const { pushErrorToast } = useToastMessageState();
  const { refreshListData, addCompanyToList } = useListUploadActions();
  const ref = useRef<HTMLDivElement>(null);
  const [isCompanySearchActive, setIsCompanySearchActive] = useState(false);
  useOutsideClick(ref, () => setIsCompanySearchActive(false));
  const { onOpenModal } = useModalState();

  const canEditLists = PermissionService.get().hasPermission(PermissionKey.canEditList);

  const handleAdd = useCallback(
    async (selectedCompany: CompanySearchResponseWithRealId) => {
      setIsCompanySearchActive(false);
      if (!activeList) {
        return;
      }
      await addCompanyToList(activeList, selectedCompany);
    },
    [activeList, addCompanyToList]
  );

  const handleSuccessBulkUpload = async () => {
    if (!activeList) {
      return;
    }
    setShowImportModal(false);
    await refreshListData(activeList);
  };

  const handleErrorBulkUpload = () => {
    pushErrorToast({ message: 'An error occurred while uploading the file' });
  };

  const setShowCreateListCompanyDialog = useSetAtom(showCreateListCompanyDialogAtom);
  const setNewListCompanyFormData = useSetAtom(newListCompanyFormDataAtom);
  const { showNewCreateCompanyFormInLists } = useFlags<MaggieFeatureFlags>();
  const onCreateListItemClick = useCallback(
    (name: string) => {
      if (showNewCreateCompanyFormInLists) {
        setNewListCompanyFormData(createCompanyDataModel({ name }));
        setShowCreateListCompanyDialog(true);
        return;
      }

      onOpenModal(<CreateListItemForm name={name} />);
    },
    [onOpenModal, setNewListCompanyFormData, setShowCreateListCompanyDialog, showNewCreateCompanyFormInLists]
  );

  const companyTemplateKey = useMemo(() => {
    if (!templatesTypeList?.templates) {
      return '';
    }

    return (
      templatesTypeList?.templates?.find((template) => template.template_key.includes('list'))
        ?.template_key ?? ''
    );
  }, [templatesTypeList?.templates]);

  if (!companyTemplateKey || !list || !canEditLists || activeList?.type !== ListType.DISCOVERY) {
    return null;
  }

  return (
    <>
      <ButtonAndSearchContainer ref={ref} outlined={isCompanySearchActive}>
        {isCompanySearchActive ? (
          <CompanySearch
            showCreateOption
            onCompanyCreateClick={(name) => onCreateListItemClick(name)}
            onChange={handleAdd}
            autoFocus={true}
            sx={{
              width: '100%',
              '& .MuiInputBase-root': { backgroundColor: 'white' },
              '& .MuiFormControl-root': { margin: 0 },
            }}
          />
        ) : (
          <Stack direction='row' gap='1rem'>
            <Button
              onClick={() => setShowImportModal(true)}
              variant='outlined'
              color='secondary'
              startIcon={<FileUploadOutlinedIcon />}
              size='medium'
            >
              Bulk Upload
            </Button>

            <Button
              onClick={() => setIsCompanySearchActive(true)}
              variant='contained'
              color='secondary'
              startIcon={<AddIcon />}
              size='medium'
            >
              Add Company
            </Button>
          </Stack>
        )}
      </ButtonAndSearchContainer>
      {showImportModal && (
        <OneSchemaImporter
          isOpen={showImportModal}
          onRequestClose={() => setShowImportModal(false)}
          clientId={oneSchemaClientId}
          userJwt={String(list.token)}
          templateKey={companyTemplateKey}
          importConfig={{ type: 'webhook', key: webhookKey }}
          devMode={isDevelopment() || isLocal()}
          className='oneschema-importer'
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: ZINDEX.UPLOAD_MODAL,
          }}
          onSuccess={handleSuccessBulkUpload}
          onError={handleErrorBulkUpload}
        />
      )}
    </>
  );
};
